import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { State, City } from 'country-state-city';
import { useRbac } from '../../context';
import { createAgencyOnboarding, getMyAgencyOnboarding, IAgencyOnboardingData } from '../../services/usersapi';
import { getCurrentUserDetails } from '../../services/apiclient';
import { toast } from 'react-toastify';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { cn } from '../../components/lib/utils';

const emptyRef = { name: '', phone: '', email: '' };
const emptyOwnerDirectorRef = { name: '', phone: '', email: '', aadhaar: '' };
const emptyHiringRef = { companyName: '', coordinatorName: '', phone: '', email: '' };
const expertCategoryOptions = [
  'BPO',
  'Information Technology',
  'Banking',
  'Insurance',
  'Aviation',
  'Retail',
  'Education',
  'Manufacturing',
  'Health Care',
  'E-commerce',
  'Hospitality',
  'Construction',
];
const countryCodeOptions = ['+91', '+1', '+44', '+61', '+65', '+971'];

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isMobile = (v: string) => /^\d{10}$/.test(v);
const isGst = (v: string) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/.test(v);
const isPan = (v: string) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v);
const isPincode = (v: string) => /^\d{6}$/.test(v);
const isAadhaar = (v: string) => /^\d{12}$/.test(v);

type AddressParts = { line1: string; city: string; state: string; pincode: string };

function buildFieldErrors(form: IAgencyOnboardingData, companyAddressParts: AddressParts): Record<string, string> {
  const e: Record<string, string> = {};
  if (!form.agencyName.trim()) e.agencyName = 'Agency Name is required';
  if (!isMobile(form.agencyPhone)) e.agencyPhone = 'Enter a valid 10 digit number';
  if (!isEmail(form.agencyEmailId)) e.agencyEmailId = 'Enter a valid email';
  if (form.companyGstNo?.trim() && !isGst(form.companyGstNo.trim().toUpperCase())) {
    e.companyGstNo = 'Invalid GST. Example: 08ABCDE9999F1Z8';
  }
  if (form.companyPan?.trim() && !isPan(form.companyPan.trim().toUpperCase())) {
    e.companyPan = 'Invalid PAN. Example: AAAPA1234C';
  }
  if (!companyAddressParts.line1.trim()) e.addressLine1 = 'Address Line 1 is required';
  if (!companyAddressParts.city.trim()) e.addressCity = 'City is required';
  if (!companyAddressParts.state.trim()) e.addressState = 'State is required';
  if (!isPincode(companyAddressParts.pincode)) e.addressPincode = 'Enter a valid 6 digit pincode';
  if (!form.ownerDirectorDetails.name.trim()) e.ownerName = 'Name is required';
  if (!form.ownerDirectorDetails.phone.trim()) e.ownerPhone = 'Phone is required';
  else if (!isMobile(form.ownerDirectorDetails.phone)) e.ownerPhone = 'Enter a valid 10 digit number';
  if (!form.ownerDirectorDetails.email.trim()) e.ownerEmail = 'Email is required';
  else if (!isEmail(form.ownerDirectorDetails.email)) e.ownerEmail = 'Enter a valid email';
  if (!form.ownerDirectorDetails.aadhaar.trim()) e.ownerAadhaar = 'Aadhaar is required';
  else if (!isAadhaar(form.ownerDirectorDetails.aadhaar)) e.ownerAadhaar = 'Enter a valid 12 digit Aadhaar';
  if (!form.establishedYear || form.establishedYear < 1900 || form.establishedYear > new Date().getFullYear()) {
    e.establishedYear = 'Enter a valid year';
  }
  if (form.currentTeamSize < 0) e.currentTeamSize = 'Cannot be negative';
  if (form.teamExpertCategories.length === 0) e.teamExpertCategories = 'Select at least one category';

  for (let i = 0; i < 3; i += 1) {
    const ref = form.currentlyHiringFor[i];
    const p = `ref${i}_`;
    if (!ref.companyName.trim()) e[`${p}companyName`] = 'Required';
    if (!ref.coordinatorName.trim()) e[`${p}coordinatorName`] = 'Required';
    if (!ref.phone.trim()) e[`${p}phone`] = 'Required';
    else if (!isMobile(ref.phone)) e[`${p}phone`] = '10 digits required';
    if (!ref.email.trim()) e[`${p}email`] = 'Required';
    else if (!isEmail(ref.email)) e[`${p}email`] = 'Invalid email';
  }

  if (!form.spocForHiring.name.trim()) e.spocHiring_name = 'Required';
  if (!form.spocForHiring.phone.trim()) e.spocHiring_phone = 'Required';
  else if (!isMobile(form.spocForHiring.phone)) e.spocHiring_phone = '10 digits required';
  if (!form.spocForHiring.email.trim()) e.spocHiring_email = 'Required';
  else if (!isEmail(form.spocForHiring.email)) e.spocHiring_email = 'Invalid email';

  if (!form.spocForBillingMis.name.trim()) e.spocBilling_name = 'Required';
  if (!form.spocForBillingMis.phone.trim()) e.spocBilling_phone = 'Required';
  else if (!isMobile(form.spocForBillingMis.phone)) e.spocBilling_phone = '10 digits required';
  if (!form.spocForBillingMis.email.trim()) e.spocBilling_email = 'Required';
  else if (!isEmail(form.spocForBillingMis.email)) e.spocBilling_email = 'Invalid email';

  if (!form.declarationAccepted) e.declaration = 'Please accept the declaration';

  return e;
}

const inputErr = (show: boolean, hasErr: boolean) =>
  `w-full rounded border p-2 ${show && hasErr ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'}`;

const AgencyOnboarding: React.FC = () => {
  const { user, setUser } = useRbac();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [existingRecord, setExistingRecord] = useState<(IAgencyOnboardingData & { _id?: string }) | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [companyAddressParts, setCompanyAddressParts] = useState({
    line1: '',
    city: '',
    state: '',
    pincode: '',
  });
  /** ISO code for selected Indian state (country-state-city); drives city list */
  const [selectedStateIsoCode, setSelectedStateIsoCode] = useState('');

  const indianStates = useMemo(
    () => [...(State.getStatesOfCountry('IN') || [])].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const indianCityNames = useMemo(() => {
    if (!selectedStateIsoCode) return [];
    const raw = City.getCitiesOfState('IN', selectedStateIsoCode) || [];
    const names = raw.map((c) => c.name);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [selectedStateIsoCode]);

  const stateSelectOptions = useMemo(
    () => indianStates.map((s) => ({ value: s.isoCode, label: s.name })),
    [indianStates]
  );

  const citySelectOptions = useMemo(
    () => indianCityNames.map((name) => ({ value: name, label: name })),
    [indianCityNames]
  );
  const [countryCodes, setCountryCodes] = useState({
    agencyPhone: '+91',
    ownerDirectorPhone: '+91',
    referencePhones: ['+91', '+91', '+91'],
    spocForHiringPhone: '+91',
    spocForBillingMisPhone: '+91',
  });
  const [form, setForm] = useState<IAgencyOnboardingData>({
    agencyName: '',
    agencyPhone: '',
    agencyEmailId: user?.email || '',
    companyGstNo: '',
    companyPan: '',
    companyAddress: '',
    establishedYear: 0,
    ownerDirectorDetails: { ...emptyOwnerDirectorRef },
    currentlyHiringFor: [{ ...emptyHiringRef }, { ...emptyHiringRef }, { ...emptyHiringRef }],
    hiringForCategory: 'IT',
    currentTeamSize: 0,
    teamExpertCategories: [],
    spocForHiring: { ...emptyRef },
    spocForBillingMis: { ...emptyRef },
    declarationAccepted: false,
  });

  const fieldErrors = useMemo(() => buildFieldErrors(form, companyAddressParts), [form, companyAddressParts]);
  const showErr = (key: string) => submitAttempted && !!fieldErrors[key];
  const errMsg = (key: string) => (showErr(key) ? fieldErrors[key] : null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        setLoadingRecord(false);
        return;
      }
      try {
        const doc = await getMyAgencyOnboarding();
        if (!cancelled) setExistingRecord(doc);
      } catch {
        if (!cancelled) setExistingRecord(null);
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const approvalStatus = existingRecord?.approvalStatus;
  const showPendingOnly =
    approvalStatus === 'PENDING' || (approvalStatus == null && !!existingRecord && !user?.isOnboarded);
  const showRejectedForm = approvalStatus === 'REJECTED';

  useEffect(() => {
    if (user?.isOnboarded) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.isOnboarded, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const errors = buildFieldErrors(form, companyAddressParts);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors below.');
      return;
    }
    if (!user?.id) {
      toast.error('User not found. Please login again.');
      return;
    }
    try {
      setIsSubmitting(true);
      const composedAddress = [
        companyAddressParts.line1.trim(),
        companyAddressParts.city.trim(),
        companyAddressParts.state.trim(),
        companyAddressParts.pincode.trim(),
      ].join(', ');
      const res = await createAgencyOnboarding(user.id, {
        ...form,
        companyGstNo: form.companyGstNo?.trim().toUpperCase() || '',
        companyPan: form.companyPan?.trim().toUpperCase() || '',
        companyAddress: composedAddress,
      });
      const created = (res as { data?: IAgencyOnboardingData & { _id?: string } })?.data;
      setExistingRecord(created || (await getMyAgencyOnboarding().catch(() => null)));
      try {
        const me = await getCurrentUserDetails();
        const u = me?.data?.user;
        if (u) setUser(u as any);
      } catch {
        /* ignore */
      }
    } catch {
      // toast handled in API helper
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateReference = (idx: number, key: 'companyName' | 'coordinatorName' | 'phone' | 'email', value: string) => {
    setForm((prev) => {
      const refs = [...prev.currentlyHiringFor];
      refs[idx] = { ...refs[idx], [key]: value };
      return { ...prev, currentlyHiringFor: refs };
    });
  };
  const updateReferenceCountryCode = (idx: number, value: string) => {
    setCountryCodes((prev) => {
      const next = [...prev.referencePhones];
      next[idx] = value;
      return { ...prev, referencePhones: next };
    });
  };

  const updateContact = (
    section: 'spocForHiring' | 'spocForBillingMis',
    key: 'name' | 'phone' | 'email',
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  if (loadingRecord) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-5xl rounded-xl border bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (existingRecord && showPendingOnly && !showRejectedForm) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-5xl rounded-xl border bg-white p-8 shadow-sm">
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Application under review</h1>
          <p className="mb-4 text-gray-600">
            Your agency onboarding for <strong>{existingRecord.agencyName}</strong> has been submitted and is waiting for
            an administrator to approve it. You will get full dashboard access after approval.
          </p>
          <p className="text-sm text-gray-500">
            Source: {existingRecord.submissionSource === 'EXTERNAL_API' ? 'External portal' : 'This portal'} · Last
            updated: {existingRecord.updatedDateTime || '—'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl rounded-xl border bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold text-gray-900">Agency Onboarding</h1>
        <p className="mb-6 text-sm text-gray-600">Please fill the details below to complete agency onboarding.</p>

        {showRejectedForm && existingRecord?.rejectionReason ? (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium">Previous application was rejected.</p>
            <p className="mt-1">{existingRecord.rejectionReason}</p>
            <p className="mt-2 text-amber-800">Please update your details and submit again.</p>
          </div>
        ) : null}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-lg border border-gray-200 p-4">
            <h2 className="mb-3 text-base font-semibold text-gray-800">Agency Basic Details</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Agency Name *</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.agencyName)}
                  placeholder="Enter agency name"
                  value={form.agencyName}
                  onChange={(e) => setForm({ ...form, agencyName: e.target.value })}
                />
                {errMsg('agencyName') ? <p className="mt-1 text-xs text-red-600">{errMsg('agencyName')}</p> : null}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Agency Phone *</label>
                <div className="flex gap-2">
                  <select
                    className="w-24 rounded border border-gray-300 p-2"
                    value={countryCodes.agencyPhone}
                    onChange={(e) => setCountryCodes((prev) => ({ ...prev, agencyPhone: e.target.value }))}
                  >
                    {countryCodeOptions.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.agencyPhone)}
                    placeholder="Enter 10 digit phone"
                    value={form.agencyPhone}
                    onChange={(e) => setForm({ ...form, agencyPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  />
                </div>
                {errMsg('agencyPhone') ? <p className="mt-1 text-xs text-red-600">{errMsg('agencyPhone')}</p> : null}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Agency Email Id *</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.agencyEmailId)}
                  placeholder="Enter email id"
                  value={form.agencyEmailId}
                  onChange={(e) => setForm({ ...form, agencyEmailId: e.target.value })}
                />
                {errMsg('agencyEmailId') ? <p className="mt-1 text-xs text-red-600">{errMsg('agencyEmailId')}</p> : null}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <h2 className="mb-3 text-base font-semibold text-gray-800">Company and Owner Details</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Company GST NO (Optional)</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.companyGstNo)}
                  placeholder="Enter GST number"
                  value={form.companyGstNo || ''}
                  onChange={(e) => setForm({ ...form, companyGstNo: e.target.value.toUpperCase() })}
                />
                <p className="mt-1 text-xs text-gray-500">Format: 08ABCDE9999F1Z8</p>
                {errMsg('companyGstNo') ? <p className="mt-1 text-xs text-red-600">{errMsg('companyGstNo')}</p> : null}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Company PAN (Optional)</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.companyPan)}
                  placeholder="Enter PAN number"
                  value={form.companyPan || ''}
                  onChange={(e) => setForm({ ...form, companyPan: e.target.value.toUpperCase() })}
                />
                <p className="mt-1 text-xs text-gray-500">Format: AAAPA1234C</p>
                {errMsg('companyPan') ? <p className="mt-1 text-xs text-red-600">{errMsg('companyPan')}</p> : null}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Established Year *</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.establishedYear)}
                  placeholder="Enter established year *"
                  type="number"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={form.establishedYear || ''}
                  onChange={(e) => setForm({ ...form, establishedYear: Number(e.target.value || 0) })}
                />
                {errMsg('establishedYear') ? <p className="mt-1 text-xs text-red-600">{errMsg('establishedYear')}</p> : null}
              </div>
              <div className="grid grid-cols-1 gap-3 md:col-span-3 md:grid-cols-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Owner / Director Name *</label>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.ownerName)}
                    placeholder="Enter name"
                    value={form.ownerDirectorDetails.name}
                    onChange={(e) =>
                      setForm({ ...form, ownerDirectorDetails: { ...form.ownerDirectorDetails, name: e.target.value } })
                    }
                  />
                  {errMsg('ownerName') ? <p className="mt-1 text-xs text-red-600">{errMsg('ownerName')}</p> : null}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Owner / Director Number *</label>
                  <div className="flex gap-2">
                    <select
                      className="w-24 rounded border border-gray-300 p-2"
                      value={countryCodes.ownerDirectorPhone}
                      onChange={(e) => setCountryCodes((prev) => ({ ...prev, ownerDirectorPhone: e.target.value }))}
                    >
                      {countryCodeOptions.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                    <input
                      className={inputErr(submitAttempted, !!fieldErrors.ownerPhone)}
                      placeholder="Enter 10 digit number"
                      value={form.ownerDirectorDetails.phone}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ownerDirectorDetails: {
                            ...form.ownerDirectorDetails,
                            phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                          },
                        })
                      }
                    />
                  </div>
                  {errMsg('ownerPhone') ? <p className="mt-1 text-xs text-red-600">{errMsg('ownerPhone')}</p> : null}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Owner / Director Email *</label>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.ownerEmail)}
                    placeholder="Enter email"
                    value={form.ownerDirectorDetails.email}
                    onChange={(e) =>
                      setForm({ ...form, ownerDirectorDetails: { ...form.ownerDirectorDetails, email: e.target.value } })
                    }
                  />
                  {errMsg('ownerEmail') ? <p className="mt-1 text-xs text-red-600">{errMsg('ownerEmail')}</p> : null}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Owner / Director Aadhaar *</label>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.ownerAadhaar)}
                    placeholder="Enter 12 digit Aadhaar"
                    value={form.ownerDirectorDetails.aadhaar}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        ownerDirectorDetails: {
                          ...form.ownerDirectorDetails,
                          aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12),
                        },
                      })
                    }
                  />
                  {errMsg('ownerAadhaar') ? <p className="mt-1 text-xs text-red-600">{errMsg('ownerAadhaar')}</p> : null}
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <h2 className="mb-3 text-base font-semibold text-gray-800">Company Address *</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Address Line 1 *</label>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.addressLine1)}
                  placeholder="Street, building, area"
                  value={companyAddressParts.line1}
                  onChange={(e) => setCompanyAddressParts((prev) => ({ ...prev, line1: e.target.value }))}
                />
                {errMsg('addressLine1') ? <p className="mt-1 text-xs text-red-600">{errMsg('addressLine1')}</p> : null}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">State *</label>
                  <SearchableSelect
                    options={stateSelectOptions}
                    value={selectedStateIsoCode}
                    onValueChange={(iso) => {
                      setSelectedStateIsoCode(iso);
                      const st = indianStates.find((s) => s.isoCode === iso);
                      setCompanyAddressParts((prev) => ({
                        ...prev,
                        state: st?.name ?? '',
                        city: '',
                      }));
                    }}
                    placeholder="Select state"
                    searchPlaceholder="Search state..."
                    emptyText="No state found."
                    className={cn(
                      'h-10 min-h-[2.5rem] rounded-md border bg-white px-3 text-sm shadow-sm',
                      submitAttempted && fieldErrors.addressState && 'border-red-500 ring-1 ring-red-500'
                    )}
                  />
                  {errMsg('addressState') ? <p className="mt-1 text-xs text-red-600">{errMsg('addressState')}</p> : null}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">City *</label>
                  <SearchableSelect
                    options={citySelectOptions}
                    value={companyAddressParts.city}
                    onValueChange={(city) => setCompanyAddressParts((prev) => ({ ...prev, city }))}
                    placeholder={selectedStateIsoCode ? 'Select city' : 'Select state first'}
                    searchPlaceholder="Search city..."
                    emptyText="No city found."
                    disabled={!selectedStateIsoCode}
                    className={cn(
                      'h-10 min-h-[2.5rem] rounded-md border bg-white px-3 text-sm shadow-sm',
                      submitAttempted && fieldErrors.addressCity && 'border-red-500 ring-1 ring-red-500'
                    )}
                  />
                  {errMsg('addressCity') ? <p className="mt-1 text-xs text-red-600">{errMsg('addressCity')}</p> : null}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Pincode *</label>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.addressPincode)}
                    placeholder="6 digit pincode"
                    value={companyAddressParts.pincode}
                    onChange={(e) =>
                      setCompanyAddressParts((prev) => ({ ...prev, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))
                    }
                  />
                  {errMsg('addressPincode') ? <p className="mt-1 text-xs text-red-600">{errMsg('addressPincode')}</p> : null}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-2 font-medium">Currently Hiring For (Give three reference) *</h2>
            <div className="space-y-3">
              {form.currentlyHiringFor.map((ref, idx) => {
                const p = `ref${idx}_`;
                return (
                  <div key={idx} className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div>
                      <input
                        className={inputErr(submitAttempted, !!fieldErrors[`${p}companyName`])}
                        placeholder={`Company ${idx + 1} name *`}
                        value={ref.companyName}
                        onChange={(e) => updateReference(idx, 'companyName', e.target.value)}
                      />
                      {errMsg(`${p}companyName`) ? (
                        <p className="mt-1 text-xs text-red-600">{errMsg(`${p}companyName`)}</p>
                      ) : null}
                    </div>
                    <div>
                      <input
                        className={inputErr(submitAttempted, !!fieldErrors[`${p}coordinatorName`])}
                        placeholder="Co-ordinator name *"
                        value={ref.coordinatorName}
                        onChange={(e) => updateReference(idx, 'coordinatorName', e.target.value)}
                      />
                      {errMsg(`${p}coordinatorName`) ? (
                        <p className="mt-1 text-xs text-red-600">{errMsg(`${p}coordinatorName`)}</p>
                      ) : null}
                    </div>
                    <div>
                      <div className="flex gap-2">
                        <select
                          className="w-24 rounded border border-gray-300 p-2"
                          value={countryCodes.referencePhones[idx] || '+91'}
                          onChange={(e) => updateReferenceCountryCode(idx, e.target.value)}
                        >
                          {countryCodeOptions.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                        <input
                          className={inputErr(submitAttempted, !!fieldErrors[`${p}phone`])}
                          placeholder="Phone no *"
                          value={ref.phone}
                          onChange={(e) => updateReference(idx, 'phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        />
                      </div>
                      {errMsg(`${p}phone`) ? <p className="mt-1 text-xs text-red-600">{errMsg(`${p}phone`)}</p> : null}
                    </div>
                    <div>
                      <input
                        className={inputErr(submitAttempted, !!fieldErrors[`${p}email`])}
                        placeholder="Email *"
                        value={ref.email}
                        onChange={(e) => updateReference(idx, 'email', e.target.value)}
                      />
                      {errMsg(`${p}email`) ? <p className="mt-1 text-xs text-red-600">{errMsg(`${p}email`)}</p> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Your Current Team Size *</label>
              <input
                className={inputErr(submitAttempted, !!fieldErrors.currentTeamSize)}
                type="number"
                min={0}
                placeholder="Enter team size *"
                value={form.currentTeamSize}
                onChange={(e) => setForm({ ...form, currentTeamSize: Number(e.target.value || 0) })}
              />
              {errMsg('currentTeamSize') ? <p className="mt-1 text-xs text-red-600">{errMsg('currentTeamSize')}</p> : null}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Hiring For Category *</label>
              <select
                className={inputErr(submitAttempted, false)}
                value={form.hiringForCategory}
                onChange={(e) => setForm({ ...form, hiringForCategory: e.target.value as 'IT' | 'NON_IT' })}
              >
                <option value="IT">Hiring For Category - IT *</option>
                <option value="NON_IT">Hiring For Category - Non IT *</option>
              </select>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="font-medium">Category in which your team is expert *</h2>
              <button
                type="button"
                className="text-sm font-medium text-orange-600 hover:text-orange-700"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    teamExpertCategories:
                      prev.teamExpertCategories.length === expertCategoryOptions.length ? [] : [...expertCategoryOptions],
                  }))
                }
              >
                {form.teamExpertCategories.length === expertCategoryOptions.length ? 'Clear all' : 'Select all'}
              </button>
            </div>
            {errMsg('teamExpertCategories') ? (
              <p className="mb-2 text-xs text-red-600">{errMsg('teamExpertCategories')}</p>
            ) : null}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              {expertCategoryOptions.map((cat) => (
                <label key={cat} className="flex h-10 items-center gap-2 rounded border border-gray-200 px-3 text-sm">
                  <input
                    type="checkbox"
                    className="accent-orange-600"
                    checked={form.teamExpertCategories.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm({ ...form, teamExpertCategories: [...form.teamExpertCategories, cat] });
                      } else {
                        setForm({ ...form, teamExpertCategories: form.teamExpertCategories.filter((x) => x !== cat) });
                      }
                    }}
                  />
                  {cat}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="mb-2 font-medium">SPOC for hiring *</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.spocHiring_name)}
                  placeholder="Name *"
                  value={form.spocForHiring.name}
                  onChange={(e) => updateContact('spocForHiring', 'name', e.target.value)}
                />
                {errMsg('spocHiring_name') ? <p className="mt-1 text-xs text-red-600">{errMsg('spocHiring_name')}</p> : null}
              </div>
              <div>
                <div className="flex gap-2">
                  <select
                    className="w-24 rounded border border-gray-300 p-2"
                    value={countryCodes.spocForHiringPhone}
                    onChange={(e) => setCountryCodes((prev) => ({ ...prev, spocForHiringPhone: e.target.value }))}
                  >
                    {countryCodeOptions.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.spocHiring_phone)}
                    placeholder="Phone *"
                    value={form.spocForHiring.phone}
                    onChange={(e) => updateContact('spocForHiring', 'phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                </div>
                {errMsg('spocHiring_phone') ? <p className="mt-1 text-xs text-red-600">{errMsg('spocHiring_phone')}</p> : null}
              </div>
              <div>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.spocHiring_email)}
                  placeholder="Email *"
                  value={form.spocForHiring.email}
                  onChange={(e) => updateContact('spocForHiring', 'email', e.target.value)}
                />
                {errMsg('spocHiring_email') ? <p className="mt-1 text-xs text-red-600">{errMsg('spocHiring_email')}</p> : null}
              </div>
            </div>

            <h2 className="mb-2 font-medium">SPOC for Billing/MIS *</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.spocBilling_name)}
                  placeholder="Name *"
                  value={form.spocForBillingMis.name}
                  onChange={(e) => updateContact('spocForBillingMis', 'name', e.target.value)}
                />
                {errMsg('spocBilling_name') ? <p className="mt-1 text-xs text-red-600">{errMsg('spocBilling_name')}</p> : null}
              </div>
              <div>
                <div className="flex gap-2">
                  <select
                    className="w-24 rounded border border-gray-300 p-2"
                    value={countryCodes.spocForBillingMisPhone}
                    onChange={(e) => setCountryCodes((prev) => ({ ...prev, spocForBillingMisPhone: e.target.value }))}
                  >
                    {countryCodeOptions.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <input
                    className={inputErr(submitAttempted, !!fieldErrors.spocBilling_phone)}
                    placeholder="Phone *"
                    value={form.spocForBillingMis.phone}
                    onChange={(e) => updateContact('spocForBillingMis', 'phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                </div>
                {errMsg('spocBilling_phone') ? (
                  <p className="mt-1 text-xs text-red-600">{errMsg('spocBilling_phone')}</p>
                ) : null}
              </div>
              <div>
                <input
                  className={inputErr(submitAttempted, !!fieldErrors.spocBilling_email)}
                  placeholder="Email *"
                  value={form.spocForBillingMis.email}
                  onChange={(e) => updateContact('spocForBillingMis', 'email', e.target.value)}
                />
                {errMsg('spocBilling_email') ? (
                  <p className="mt-1 text-xs text-red-600">{errMsg('spocBilling_email')}</p>
                ) : null}
              </div>
            </div>
          </div>

          <label
            className={`flex items-start gap-2 rounded border p-3 text-sm ${
              showErr('declaration') ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
            }`}
          >
            <input
              type="checkbox"
              className="mt-1 accent-orange-600"
              checked={form.declarationAccepted}
              onChange={(e) => setForm({ ...form, declarationAccepted: e.target.checked })}
            />
            <span>
              I agree that we will not charge any fees from candidates at any stage of the hiring process. * I understand
              that if EarlyJobs discovers any violation of this agreement, Earlyjobs reserve the right to take appropriate
              legal action against the responsible party.
            </span>
          </label>
          {errMsg('declaration') ? <p className="text-xs text-red-600">{errMsg('declaration')}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded bg-orange-600 px-5 py-2.5 text-white hover:bg-orange-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Onboarding'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AgencyOnboarding;
